import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList, faFileAlt, faChartBar,
  faCalendarDay, faExclamationTriangle, faVideo, faUsers,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../../../app/Layout';
import HeaderContent from '../../General/HeaderContent';
import Breadcrumb from '../../General/Breadcrumb';
import SpinnerLoading from '../../../components/Spinners/SpinnerLoading';
import SimpleSelect from '../../../components/plugins/SimpleSelect';
import { obtenerEventosTutor } from '../../../../actions/tutoresActions';
import { obtenerProgreso } from '../../../../actions/tutoriaHU9Actions';

const TABS = [
  { key: 'progreso',  label: 'Progreso',         icon: faChartBar      },
  { key: 'protocolo', label: 'Protocolo',         icon: faClipboardList },
  { key: 'reporte',   label: 'Reporte de sesión', icon: faFileAlt       },
];

const ESTADO_REPORTE = {
  enviado:     { label: 'Enviado',   variant: 'success'   },
  borrador:    { label: 'Borrador',  variant: 'warning'   },
  sin_reporte: { label: 'Pendiente', variant: 'secondary' },
};

const ESTADO_PROTOCOLO = {
  completado:  { label: 'Completado',  variant: 'success'   },
  en_proceso:  { label: 'En proceso',  variant: 'primary'   },
  pendiente:   { label: 'Pendiente',   variant: 'secondary' },
};

const ResCard = ({ icon, color, valor, label, onClick }) => (
  <div className="col-6 col-md-2 mb-2" onClick={onClick} style={onClick ? { cursor: 'pointer' } : {}}>
    <div className="box p-2 text-center h-100" style={{ borderTop: `3px solid ${color}` }}>
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 20 }} className="mb-1" />
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{valor}</div>
      <small className="text-muted">{label}</small>
    </div>
  </div>
);

const TIPOS_INC = [
  { key: 'tecnica',   label: 'Técnica'      },
  { key: 'academica', label: 'Académica'    },
  { key: 'particip',  label: 'Participación'},
  { key: 'otra',      label: 'Otra'         },
];

const ModalIncidencias = ({ sesion, onClose }) => {
  if (!sesion) return null;
  const tieneAlguna = TIPOS_INC.some(t => sesion[`incidencia_${t.key}_desc`]);
  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning mr-2" />
          Incidencias — {sesion.nombre_sesion}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          <small>Sesión del {sesion.fecha_sesion} · {sesion.nombre_docente || 'Docente no registrado'}</small>
        </p>
        {!tieneAlguna ? (
          <p className="text-muted text-center py-3">Sin incidencias registradas</p>
        ) : (
          TIPOS_INC.map(t => {
            const desc  = sesion[`incidencia_${t.key}_desc`];
            const resol = sesion[`incidencia_${t.key}_resol`];
            if (!desc) return null;
            return (
              <div key={t.key} className="mb-3 p-3 border rounded" style={{ borderLeft: '4px solid #ffc107 !important' }}>
                <strong className="d-block mb-1">{t.label}</strong>
                <div className="mb-1">
                  <small className="text-muted">Descripción:</small>
                  <p className="mb-1">{desc}</p>
                </div>
                {resol && (
                  <div>
                    <small className="text-muted">Resolución:</small>
                    <p className="mb-0 text-success">{resol}</p>
                  </div>
                )}
                {!resol && (
                  <small className="text-danger">Sin resolución registrada</small>
                )}
              </div>
            );
          })
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const EspacioTrabajo = () => {
  const dispatch = useDispatch();

  const { usuario }                           = useSelector(s => s.usuario);
  const { eventosTutor, loading: loadEv }     = useSelector(s => s.tutores);
  const { sesionesProgreso, resumenProgreso, loadingProgreso } = useSelector(s => s.tutoriaHU9);

  const eventos = eventosTutor?.eventos_tutor || [];
  const [tabActiva,    setTabActiva]    = useState('progreso');
  const [proyectoSel,  setProyectoSel]  = useState(null);
  const [incidenciaSel, setIncidenciaSel] = useState(null);
  const sesionHoyRef = useRef(null);
  const [scrollToHoy, setScrollToHoy] = useState(false);

  const irASesionesHoy = useCallback(() => {
    setTabActiva('progreso');
    setScrollToHoy(true);
  }, []);

  useEffect(() => {
    if (scrollToHoy && tabActiva === 'progreso') {
      const timer = setTimeout(() => {
        if (sesionHoyRef.current) {
          sesionHoyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setScrollToHoy(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [scrollToHoy, tabActiva]);

  useEffect(() => {
    if (usuario.id_empleado) {
      dispatch(obtenerEventosTutor(usuario.id_empleado));
    }
  }, [usuario.id_empleado]);

  useEffect(() => {
    if (eventos.length === 1) setProyectoSel(eventos[0]);
  }, [eventos]);

  useEffect(() => {
    if (proyectoSel) {
      dispatch(obtenerProgreso(proyectoSel.id_tutor_proyecto));
    }
  }, [proyectoSel]);

  const sesiones = sesionesProgreso || [];
  const resumen  = resumenProgreso  || {};

  const opcionesProyecto = eventos.map(ev => ({
    value: ev.id_tutor_proyecto,
    label: `${ev.id_proyecto} - ${ev.nombre_proyecto || `Proyecto ${ev.id_tutor_proyecto}`}`,
  }));

  const valorProyectoSel = proyectoSel
    ? { value: proyectoSel.id_tutor_proyecto, label: `${proyectoSel.id_proyecto} - ${proyectoSel.nombre_proyecto || `Proyecto ${proyectoSel.id_tutor_proyecto}`}` }
    : null;

  const seleccionarProyecto = (opcion) => {
    if (!opcion || opcion.value === undefined) { setProyectoSel(null); return; }
    const ev = eventos.find(ev => ev.id_tutor_proyecto === opcion.value);
    setProyectoSel(ev || null);
  };

  // Parsea 'YYYY-MM-DD' como fecha local sin desfase de zona horaria
  const parseFechaLocal = (f) => {
    if (!f) return null;
    const [y, m, d] = f.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const renderTabProgreso = () => {
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    const manana = new Date(hoy); manana.setDate(manana.getDate() + 1);
    const sesionesOrdenadas = sesiones.slice().sort((a, b) => parseFechaLocal(a.fecha_orden) - parseFechaLocal(b.fecha_orden));
    const idxProxima = sesionesOrdenadas.findIndex(s => parseFechaLocal(s.fecha_orden) >= hoy);
    return (
    <div className="table-responsive">
      <table className="table table-bordered table-hover table-sm">
        <thead className="thead-light">
          <tr>
            <th>#</th>
            <th>Programa / Módulo</th>
            <th>Clase</th>
            <th>Docente</th>
            <th className="text-center">Fecha</th>
            <th className="text-center">Horario</th>
            <th className="text-center">Asistencia</th>
            <th className="text-center">Protocolo</th>
            <th className="text-center">Reporte</th>
            <th className="text-center">Inc.</th>
            <th className="text-center">Grabación</th>
            <th className="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sesionesOrdenadas.length === 0 ? (
            <tr><td colSpan={12} className="text-center text-muted py-3">No hay sesiones asignadas</td></tr>
          ) : (
            sesionesOrdenadas.map((s, i) => {
              const estRep  = ESTADO_REPORTE[s.estado_reporte]    || ESTADO_REPORTE.sin_reporte;
              const estProt = ESTADO_PROTOCOLO[s.estado_protocolo] || ESTADO_PROTOCOLO.pendiente;
              const pctAsist = s.alumnos_inscritos > 0
                ? Math.round((s.alumnos_conectados / s.alumnos_inscritos) * 100)
                : null;
              const tieneInc = parseInt(s.tiene_incidencia) === 1;
              const esProxima = i === idxProxima;
              const esSesionHoy = s.fecha_orden && parseFechaLocal(s.fecha_orden) >= hoy && parseFechaLocal(s.fecha_orden) < manana;
              return (
                <tr key={s.id_sesion} ref={esSesionHoy ? sesionHoyRef : null} className={tieneInc ? 'table-warning' : ''} style={esProxima && !tieneInc ? { backgroundColor: '#d4edda' } : {}}>
                  <td className="text-center align-middle">{i + 1}</td>
                  <td className="align-middle">
                    <small className="d-block text-muted">{s.nombre_proyecto || '—'}</small>
                    <small>{s.nombre_modulo || '—'}</small>
                  </td>
                  <td className="align-middle">
                    <small>{s.nombre_sesion}</small>
                    {esProxima && <span className="badge badge-success ml-2" style={{ fontSize: 10 }}>Próxima</span>}
                  </td>
                  <td className="align-middle"><small>{s.nombre_docente || '—'}</small></td>
                  <td className="text-center align-middle"><small>{s.fecha_sesion}</small></td>
                  <td className="text-center align-middle"><small>{s.horario_proyecto || '—'}</small></td>
                  <td className="text-center align-middle">
                    {pctAsist !== null
                      ? <span style={{ color: pctAsist >= 80 ? '#28a745' : '#dc3545', fontWeight: 600 }}>{pctAsist}%</span>
                      : <span className="text-muted">—</span>
                    }
                  </td>
                  <td className="text-center align-middle">
                    <Badge variant={estProt.variant}>{estProt.label}</Badge>
                    {s.pct_protocolo != null && (
                      <small className="d-block text-muted">{s.pct_protocolo}%</small>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    <Badge variant={estRep.variant}>{estRep.label}</Badge>
                  </td>
                  <td className="text-center align-middle">
                    {tieneInc ? (
                      <button
                        className="btn btn-xs btn-warning"
                        title="Ver incidencias"
                        onClick={() => setIncidenciaSel(s)}
                      >
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                      </button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    {parseInt(s.grabacion_vimeo) === 1
                      ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Grabación subida a Vimeo</Tooltip>}>
                          <span><FontAwesomeIcon icon={faVideo} className="text-success" /></span>
                        </OverlayTrigger>
                      ) : (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Grabación pendiente — no subida a Vimeo</Tooltip>}>
                          <span><FontAwesomeIcon icon={faVideo} className="text-danger" /></span>
                        </OverlayTrigger>
                      )
                    }
                  </td>
                  <td className="text-center align-middle" style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/tutor/sesion/${s.id_sesion}/protocolo`} className="btn btn-info btn-xs mr-1" title="Protocolo">
                      <FontAwesomeIcon icon={faClipboardList} />
                    </Link>
                    <Link to={`/tutor/sesion/${s.id_sesion}/reporte`} className="btn btn-primary btn-xs" title="Reporte">
                      <FontAwesomeIcon icon={faFileAlt} />
                    </Link>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );};

  const renderTabProtocolo = () => {
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    const pendientes = sesiones.filter(s => s.estado_protocolo !== 'completado').slice().sort((a, b) => parseFechaLocal(a.fecha_orden) - parseFechaLocal(b.fecha_orden));
    const idxProxima = pendientes.findIndex(s => parseFechaLocal(s.fecha_orden) >= hoy);
    return (
    <div className="table-responsive">
      {pendientes.length === 0 && (
        <div className="alert alert-success m-3">
          Todos los protocolos están completados.
        </div>
      )}
      <table className="table table-bordered table-hover table-sm">
        <thead className="thead-light">
          <tr>
            <th>#</th>
            <th>Sesión</th>
            <th>Fecha</th>
            <th className="text-center">Estado</th>
            <th className="text-center">Avance</th>
            <th className="text-center">Acción</th>
          </tr>
        </thead>
        <tbody>
          {pendientes.length === 0 ? (
            <tr><td colSpan={6} className="text-center text-muted">Sin protocolos pendientes</td></tr>
          ) : (
            pendientes.map((s, i) => {
              const estProt = ESTADO_PROTOCOLO[s.estado_protocolo] || ESTADO_PROTOCOLO.pendiente;
              const esProxima = i === idxProxima;
              return (
                <tr key={s.id_sesion} style={esProxima ? { backgroundColor: '#d4edda' } : {}}>
                  <td className="text-center align-middle">{i + 1}</td>
                  <td className="align-middle">
                    <small>{s.nombre_sesion}</small>
                    {esProxima && <span className="badge badge-success ml-2" style={{ fontSize: 10 }}>Próxima</span>}
                  </td>
                  <td className="text-center align-middle"><small>{s.fecha_sesion}</small></td>
                  <td className="text-center align-middle"><Badge variant={estProt.variant}>{estProt.label}</Badge></td>
                  <td className="text-center align-middle">
                    <small>{s.pct_protocolo != null ? `${s.pct_protocolo}%` : '—'}</small>
                  </td>
                  <td className="text-center align-middle">
                    <Link to={`/tutor/sesion/${s.id_sesion}/protocolo`} className="btn btn-info btn-sm">
                      <FontAwesomeIcon icon={faClipboardList} className="mr-1" />
                      Abrir
                    </Link>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  )};

  const renderTabReporte = () => {
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    const pendientes = sesiones.filter(s => s.estado_reporte !== 'enviado').slice().sort((a, b) => parseFechaLocal(a.fecha_orden) - parseFechaLocal(b.fecha_orden));
    const idxProxima = pendientes.findIndex(s => parseFechaLocal(s.fecha_orden) >= hoy);
    return (
    <div className="table-responsive">
      {pendientes.length === 0 && (
        <div className="alert alert-success m-3">
          Todos los reportes han sido enviados.
        </div>
      )}
      <table className="table table-bordered table-hover table-sm">
        <thead className="thead-light">
          <tr>
            <th>#</th>
            <th>Sesión</th>
            <th>Fecha</th>
            <th className="text-center">Estado</th>
            <th className="text-center">Chat</th>
            <th className="text-center">Grabación</th>
            <th className="text-center">Acción</th>
          </tr>
        </thead>
        <tbody>
          {pendientes.length === 0 ? (
            <tr><td colSpan={7} className="text-center text-muted">Sin reportes pendientes</td></tr>
          ) : (
            pendientes.map((s, i) => {
              const estRep = ESTADO_REPORTE[s.estado_reporte] || ESTADO_REPORTE.sin_reporte;
              const esProxima = i === idxProxima;
              return (
                <tr key={s.id_sesion} style={esProxima ? { backgroundColor: '#d4edda' } : {}}>
                  <td className="text-center align-middle">{i + 1}</td>
                  <td className="align-middle">
                    <small>{s.nombre_sesion}</small>
                    {esProxima && <span className="badge badge-success ml-2" style={{ fontSize: 10 }}>Próxima</span>}
                  </td>
                  <td className="text-center align-middle"><small>{s.fecha_sesion}</small></td>
                  <td className="text-center align-middle"><Badge variant={estRep.variant}>{estRep.label}</Badge></td>
                  <td className="text-center align-middle">
                    {parseInt(s.recursos_listos) === 1
                      ? <Badge variant="success">OK</Badge>
                      : <Badge variant="secondary">—</Badge>
                    }
                  </td>
                  <td className="text-center align-middle">
                    {parseInt(s.grabacion_vimeo) === 1
                      ? <Badge variant="success">Subida</Badge>
                      : <Badge variant="danger">Pendiente</Badge>
                    }
                  </td>
                  <td className="text-center align-middle">
                    <Link to={`/tutor/sesion/${s.id_sesion}/reporte`} className="btn btn-primary btn-sm">
                      <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                      Abrir
                    </Link>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );};

  return (
    <Layout>
      <HeaderContent title="Panel" icon="briefcase" />
      <Breadcrumb link={[
        { url: '/inicio', nombre: 'Inicio', icon: 'fas fa-home' },
        { url: '',        nombre: 'Espacio de Trabajo' },
      ]} />

      {/* Selector de proyecto */}
      <div className="box p-3 mb-3">
        <div className="row align-items-end">
          <div className="col-md-5">
            <label><strong>Proyecto</strong></label>
            {loadEv ? (
              <p className="text-muted">Cargando proyectos...</p>
            ) : (
              <SimpleSelect
                options={opcionesProyecto}
                getSelection={seleccionarProyecto}
                defaultValue={valorProyectoSel}
                placeholder="Buscar por nombre o ID..."
                isClearable
              />
            )}
          </div>
          {proyectoSel && !loadingProgreso && (
            <div className="col-md-4">
              <small className="text-muted">{sesiones.length} sesión(es)</small>
            </div>
          )}
        </div>
      </div>

      {/* Cards de resumen */}
      {proyectoSel && resumen && (
        <div className="row mb-2">
          <ResCard icon={faCalendarDay}         color="#007bff" valor={resumen.sesiones_hoy           ?? 0} label="Sesiones hoy"            onClick={resumen.sesiones_hoy > 0 ? irASesionesHoy : undefined} />
          <ResCard icon={faClipboardList}       color="#fd7e14" valor={resumen.protocolos_pendientes  ?? 0} label="Protocolos pendientes"   />
          <ResCard icon={faFileAlt}             color="#dc3545" valor={resumen.reportes_pendientes    ?? 0} label="Reportes pendientes"     />
          <ResCard icon={faExclamationTriangle} color="#ffc107" valor={resumen.con_incidencias        ?? 0} label="Con incidencias"         />
          <ResCard icon={faVideo}               color="#6f42c1" valor={resumen.grabaciones_pendientes ?? 0} label="Grabaciones pendientes"  />
          <ResCard icon={faUsers}               color="#28a745" valor={`${resumen.asistencia_promedio ?? 0}%`} label="Asistencia promedio" />
        </div>
      )}

      {/* Tabs */}
      <div className="box">
        <div className="box-header p-0">
          <ul className="nav nav-tabs" style={{ borderBottom: 'none', paddingLeft: 16 }}>
            {TABS.map(tab => (
              <li key={tab.key} className="nav-item">
                <button
                  className={`nav-link btn btn-link ${tabActiva === tab.key ? 'active' : ''}`}
                  onClick={() => setTabActiva(tab.key)}
                  style={{ border: 'none', background: 'none' }}
                >
                  <FontAwesomeIcon icon={tab.icon} className="mr-1" />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="box-body">
          {!proyectoSel ? (
            <p className="text-muted text-center py-3">Selecciona un proyecto para ver las sesiones</p>
          ) : loadingProgreso ? (
            <SpinnerLoading loading={true} />
          ) : (
            <>
              {tabActiva === 'progreso'  && renderTabProgreso()}
              {tabActiva === 'protocolo' && renderTabProtocolo()}
              {tabActiva === 'reporte'   && renderTabReporte()}
            </>
          )}
        </div>
      </div>

      <ModalIncidencias sesion={incidenciaSel} onClose={() => setIncidenciaSel(null)} />
    </Layout>
  );
};

export default EspacioTrabajo;
